import React, { useEffect, useState } from 'react';
import { collection, doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { getUserById } from '../services/userService';

const badgeMeta = {
    crown: { emoji: '👑', label: 'Crown' },
    diamond: { emoji: '💎', label: 'Diamond' },
    gold: { emoji: '🥇', label: 'Gold' },
    silver: { emoji: '🥈', label: 'Silver' },
    bronze: { emoji: '🥉', label: 'Bronze' },
};

const BadgeCollection = ({ uid, selectedBadgeId, onClose }) => {
    const [badges, setBadges] = useState([]);
    const [savingId, setSavingId] = useState(null);

    useEffect(() => {
        if (!uid) return () => {};

        const badgesRef = collection(db, 'users', uid, 'badges');
        return onSnapshot(badgesRef, async (snapshot) => {
            const entries = await Promise.all(snapshot.docs.map(async (badgeDoc) => {
                const data = badgeDoc.data() || {};
                let artistName = data.artistName || data.artistDisplayName;
                if (!artistName) {
                    const artist = await getUserById(badgeDoc.id);
                    artistName = artist?.name || artist?.displayName || 'Artist';
                }
                const type = String(data.badgeType || 'bronze').toLowerCase();
                const meta = badgeMeta[type] || badgeMeta.bronze;
                return {
                    id: badgeDoc.id,
                    artistName,
                    type,
                    emoji: meta.emoji,
                    label: meta.label,
                    tipCount: Number(data.tipCount || 0),
                    title: `${meta.label} Fan`,
                };
            }));
            entries.sort((a, b) => b.tipCount - a.tipCount);
            setBadges(entries);
        }, (error) => {
            console.error('Badge collection listener failed:', error);
            setBadges([]);
        });
    }, [uid]);

    const selectBadge = async (badge) => {
        setSavingId(badge.id);
        try {
            await updateDoc(doc(db, 'users', uid), {
                selectedBadgeId: badge.id,
                selectedBadgeType: badge.type,
                selectedBadgeTitle: `${badge.emoji} ${badge.title} ${badge.artistName}`,
            });
        } catch (error) {
            console.error('Failed to select badge:', error);
        } finally {
            setSavingId(null);
        }
    };

    return (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/75 px-4">
            <div className="max-h-[82vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-[#171717] p-5 text-white shadow-2xl ring-1 ring-white/15">
                <div className="mb-5 flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-xl font-bold">Badge Collection</h2>
                        <p className="mt-1 text-sm text-white/60">Apna featured badge select karein.</p>
                    </div>
                    <button type="button" onClick={onClose} className="text-2xl leading-none text-white/60 hover:text-white" aria-label="Close badge collection">
                        ×
                    </button>
                </div>

                {badges.length === 0 ? (
                    <div className="rounded-2xl bg-white/5 p-6 text-center text-sm text-white/60">
                        Abhi koi badge nahi hai. Artists ko tip karke badges collect karein.
                    </div>
                ) : (
                    <div className="grid gap-3 sm:grid-cols-2">
                        {badges.map((badge) => {
                            const selected = selectedBadgeId === badge.id;
                            return (
                                <button
                                    key={badge.id}
                                    type="button"
                                    onClick={() => selectBadge(badge)}
                                    disabled={savingId === badge.id}
                                    className={`rounded-2xl p-4 text-left transition ${
                                        selected
                                            ? 'bg-red-500/20 ring-2 ring-red-400'
                                            : 'bg-white/5 ring-1 ring-white/10 hover:bg-white/10'
                                    }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <span className="text-3xl">{badge.emoji}</span>
                                        <span className="min-w-0 flex-1">
                                            <span className="block font-semibold">{badge.title}</span>
                                            <span className="block truncate text-sm text-white/60">{badge.artistName}</span>
                                        </span>
                                        <span className="text-xs text-white/50">{badge.tipCount} tips</span>
                                    </div>
                                    <div className="mt-3 text-xs font-semibold text-red-300">
                                        {savingId === badge.id ? 'Saving...' : selected ? 'Selected badge' : 'Use this badge'}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BadgeCollection;
